import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { NOT_FOUND } from '../../../../shared/errors/errorMessage';
import { titleCase } from '../../../../shared/utils';
import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  ButtonGroup,
  FormGroup,
  Icon,
  icons,
  NavigationButton,
  SearchInput,
} from '../../../components/generic';
import Button from '../../../components/generic/Button';
import Flex from '../../../components/generic/Flex';
import List from '../../../components/generic/List';
import Loader from '../../../components/generic/Loader';
import Text from '../../../components/generic/Text';
import Box from '../../generics/Box';
import { latestReleaseRoute, releaseReportRoute } from '../../helpers/routes';
import { useSlug, useUser } from '../../hooks';
import EmptyState from '../EmptyState';
import ErrorState from '../ErrorState';
import { useReleaseGroups, useTitleSearch } from '../hooks';
import type { ReleaseGroupsResponse } from '../../../../shared/publicPortal/types';

function renderReleaseGroupList({
  releaseGroups,
  errors,
  isLoading,
  slug,
}: {
  releaseGroups: ReleaseGroupsResponse | null;
  errors: string[] | null;
  isLoading: boolean;
  slug: string;
}) {
  if (isLoading) {
    return (
      <Box>
        <Loader />
      </Box>
    );
  }

  if (!_.isEmpty(errors) && !errors?.includes(NOT_FOUND)) {
    return (
      <Box>
        <ErrorState />
      </Box>
    );
  }

  if (!releaseGroups || _.isEmpty(releaseGroups?.releaseGroups)) {
    return (
      <Box>
        <EmptyState />
      </Box>
    );
  }

  return releaseGroups?.releaseGroups.map((releaseGroup) => (
    <List.RowContainer key={releaseGroup.releaseGroupId}>
      <List.Row
        right={
          <ButtonGroup spacing="xs">
            <NavigationButton
              to={releaseReportRoute(slug, releaseGroup.releaseGroupReleaseId)}
              type={BUTTON_TYPE.OUTLINE}
              size={BUTTON_SIZE.LARGE}
              icon={icons.downloadRegular}
              openInNewTab
              stopPropagation
            >
              Download
            </NavigationButton>
            <NavigationButton
              to={latestReleaseRoute(slug, releaseGroup.releaseGroupId)}
              size={BUTTON_SIZE.LARGE}
              icon={icons.eyeRegular}
            >
              View
            </NavigationButton>
          </ButtonGroup>
        }
        to={latestReleaseRoute(slug, releaseGroup.releaseGroupId)}
      >
        <List.RowTitle>{releaseGroup.releaseGroupTitle}</List.RowTitle>
        <List.RowDetails>
          <List.RowDetailsItem>Version {releaseGroup.releaseGroupReleaseTitle}</List.RowDetailsItem>
        </List.RowDetails>
      </List.Row>
    </List.RowContainer>
  ));
}

type Props = {
  visibility: 'public' | 'private';
};

const ReleaseGroupList: VoidFunctionComponent<Props> = ({ visibility }: Props) => {
  const slug = useSlug();
  const { search, setSearch, debouncedSearch } = useTitleSearch();
  const { isTokenAuthenticated, removeToken } = useUser();
  const shouldDisplaySignOutTokenButton = visibility === 'private' && isTokenAuthenticated;
  const { isLoading, errors, releaseGroups } = useReleaseGroups({
    isPublic: visibility === 'public',
    search: debouncedSearch,
  });

  return (
    <>
      <Flex justify="space-between">
        <Text.H3>
          <img src="/img/list-tree-duotone.svg" alt="list" /> {titleCase(visibility)} SBOMs
        </Text.H3>
        {shouldDisplaySignOutTokenButton && (
          <Button
            type={BUTTON_TYPE.OUTLINE}
            onClick={() => {
              removeToken();
            }}
          >
            <Icon icon={icons.powerOff} /> Sign out token
          </Button>
        )}
      </Flex>
      <FormGroup>
        <SearchInput label="search" placeholder="Search" onChange={(value) => setSearch(value)} value={search} />
      </FormGroup>
      <List>
        <List.Body hasOverflowAuto>{renderReleaseGroupList({ isLoading, errors, releaseGroups, slug })}</List.Body>
      </List>
    </>
  );
};

export default ReleaseGroupList;
